import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import {
  addMorphemeToCollection,
  removeMorphemeFromCollection,
} from "@-/dev.improve.haniverse.lib.collection.morpheme";
import { ensureFavoritesCollection } from "@-/dev.improve.haniverse.lib.morpheme.collection.favorites.ensure";
import { useMutation } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { GraphemeMorphemePanelsContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";

/**
 * Toggles the panel character in the Favorites collection, creating that collection on first use.
 * Shared by the toolbar Favorite pill and the native card actions menu.
 */
export function useMorphemeFavoriteToggle() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const [favoritesCollectionId, grapheme, isFavorited] = useContextSelectors(
    GraphemeMorphemePanelsContext,
    (c) => c.userSlice.data.favoritesCollectionId,
    (c) => c.readonlySlice.data.grapheme,
    (c) => c.userSlice.data.isFavorited,
  );

  const toggleFavoriteMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      // Create the Favorites collection LAZILY, on the first star.
      //
      // Opening the app must not write to the database: an eager create gave every
      // fresh profile a `collection` row before the user did anything, which made
      // `hasLocalStudyData` report study data for someone who had studied nothing and
      // fired the sign-in replace-warning for EVERY user.
      //
      // The collection and each membership have ids derived from what they are, so racing this
      // (two stars tapped together, or on two devices) resolves to the same rows, never duplicates.
      const favorites =
        favoritesCollectionId === null ? await ensureFavoritesCollection(actorDb) : null;
      const collectionId = favorites?.id ?? favoritesCollectionId;
      if (collectionId === null) {
        throw new Error("The Favorites collection could not be resolved");
      }
      await actorDb(import.meta.location).transaction(async (tx) => {
        const membership = await tx.query.collectionMorpheme.findFirst({
          columns: { id: true },
          where: { collectionId, morpheme: grapheme },
        });
        await (membership === undefined
          ? addMorphemeToCollection(tx, {
              collectionId,
              morpheme: grapheme,
              nowIso: new Date().toISOString(),
            })
          : removeMorphemeFromCollection(tx, { collectionId, morpheme: grapheme }));
      });
    },
    mutationKey: ["morphemeStarToggle"],
  });

  return {
    isFavorited,
    isPending: toggleFavoriteMutation.isPending,
    toggle: () => toggleFavoriteMutation.mutate(),
  };
}
